import React, { useState, useEffect } from 'react';
import { useNotifications } from '../../hooks/useNotifications';
import {
  trackPromptShown,
  trackPromptInteraction,
  trackPermissionRequested,
  trackPermissionResult
} from '../../utils/notifications/notificationAnalytics';
import './NotificationOptInPrompt.css';

interface NotificationOptInPromptProps {
  className?: string;
}

const NotificationOptInPrompt: React.FC<NotificationOptInPromptProps> = ({ className }) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const {
    isSupported,
    permission,
    requestPermission,
    subscribe
  } = useNotifications();

  useEffect(() => {
    // Check if we should show the prompt
    const checkPromptVisibility = () => {
      // Don't show if notifications are not supported
      if (!isSupported) return;

      // Don't show if permission is already granted
      if (permission === 'granted') return;

      // Don't show if permission is denied (browser level)
      if (permission === 'denied') return;

      // Check if we've already shown the prompt
      const hasShownPrompt = localStorage.getItem('notification_prompt_shown');
      if (hasShownPrompt) return;

      // Check if this is not the first visit
      const hasVisited = sessionStorage.getItem('visited');
      if (!hasVisited) {
        // Mark as visited for next time
        sessionStorage.setItem('visited', 'true');
        return;
      }

      // Show the prompt after a delay
      setTimeout(() => {
        setIsVisible(true);
        localStorage.setItem('notification_prompt_shown_time', Date.now().toString());
        trackPromptShown();
      }, 10000); // Show after 10 seconds
    };

    checkPromptVisibility();
  }, [isSupported, permission]);

  const handleEnable = async () => {
    try {
      trackPromptInteraction();
      trackPermissionRequested();
      
      // Request permission
      const newPermission = await requestPermission();
      
      // Track the result
      trackPermissionResult(newPermission);
      
      // If granted, subscribe to push notifications
      if (newPermission === 'granted') {
        await subscribe();
      }
      
      // Mark as shown regardless of outcome
      localStorage.setItem('notification_prompt_shown', 'true');
      setIsVisible(false);
    } catch (error) {
      console.error('Error enabling notifications:', error);
      setIsVisible(false);
    }
  };

  const handleLater = () => {
    trackPromptInteraction();
    
    // Set a shorter cooldown period (2 hours)
    const laterCooldownPeriod = 2 * 60 * 60 * 1000; // 2 hours
    const cooldownUntil = Date.now() + laterCooldownPeriod;
    
    localStorage.setItem('notification_prompt_cooldown', cooldownUntil.toString());
    setIsVisible(false);
  };

  const handleDismiss = () => {
    trackPromptInteraction();
    
    // Mark as shown so we don't show it again
    localStorage.setItem('notification_prompt_shown', 'true');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className={`notification-prompt-overlay ${className || ''}`}>
      <div className="notification-prompt-container">
        <div className="notification-prompt-icon">🔔</div>
        <h2 className="notification-prompt-title">Stay updated with VeriPay</h2>
        <p className="notification-prompt-message">
          Enable notifications to receive important updates about your transactions, 
          account security, and payment status changes.
        </p>
        <div className="notification-prompt-buttons">
          <button 
            className="notification-prompt-button-primary"
            onClick={handleEnable}
          >
            Enable Notifications
          </button>
          <button 
            className="notification-prompt-button-secondary"
            onClick={handleLater}
          >
            Maybe Later
          </button>
          <button 
            className="notification-prompt-button-tertiary"
            onClick={handleDismiss}
          >
            Don't Ask Again
          </button>
        </div>
        <button 
          className="notification-prompt-close"
          onClick={handleDismiss}
          aria-label="Close"
        >
          ✕
        </button>
      </div>
    </div>
  );
};

export default NotificationOptInPrompt;